// Copyright (c) Docugami, Inc. All rights reserved.

import { TokenRange } from "networking/api";
import { useCallback } from "react";
import { AppDispatch, AppState } from "store";
import { folderIdSelector, selectTokens, selectMappedTokens } from "store/dgml/dgmlSelectors";
import { useEditTextMutation } from "store/frontend/frontendSlice";
import { TokensTextParser } from "utils/TokensTextParser";
import { useAppDispatch } from "./redux";

export interface ITextEditCommands {
    trigger: (tokenRange: TokenRange, text: string) => void;
    isLoading: boolean;
}

export const useTextEditCommands = (documentId: string): ITextEditCommands => {
    const [trigger, state] = useEditTextMutation();

    const dispatch = useAppDispatch();
    const triggerEdit = useCallback(async (tokenRange: TokenRange, text: string) => {
        const request = dispatch(triggerTextEditThunk(documentId, tokenRange, text))
        await trigger(request);
    }, [dispatch, trigger, documentId]);

    return {
        trigger: triggerEdit,
        isLoading: state.isLoading
    }
}

const triggerTextEditThunk = (documentId: string, tokenRange: TokenRange, text: string) => (dispatch: AppDispatch, getState: () => AppState) => {
    const folderId = folderIdSelector(documentId);
    if (!folderId) {
        throw new Error("Invalid document ID for text edit.");
    }

    if (tokenRange.fromTok === tokenRange.toTok) {
        throw new Error("Invalid token range.")
    }

    const state = getState();
    const tokens = selectTokens(state, folderId.clusterId, folderId.fileId);
    if (!tokens?.length) {
        throw new Error("Cannot edit text before tokens have loaded.")
    }

    // For now, we will maintain whatever the implied whitespace is between the target token range and the subsequent one
    const nextToken = tokens[tokenRange.toTok];
    const hasTrailingSpace = !!nextToken && TokensTextParser.hasImpliedSpace(tokens[tokenRange.toTok - 1], nextToken);
    text = text.trim();
    if (hasTrailingSpace) {
        text += ' ';
    }

    const mappedTokens = selectMappedTokens(state, folderId.clusterId, folderId.fileId);
    return {
        docsetId: folderId.clusterId,
        fileId: folderId.fileId,
        tokenKeyRange: mappedTokens.tokenRangeToTokenKeyRange(tokenRange),
        text: text
    };
}
